/* eslint-disable */
// Local styles implementation
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import moment from "moment";
import PerfectScrollbar from "perfect-scrollbar";

// @material-ui/icons
import { Check, Clear, PostAdd, OpenInBrowser, DirectionsWalk } from "@material-ui/icons";

// core components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button";
import Tooltip from "@material-ui/core/Tooltip";
import InitiativesTimeline from "components/Initiatives/InitiativesTimeline";

// styles
import { makeStyles } from "@material-ui/core/styles";
import {
  grayColor,
  blendColors,
  successColor,
  dangerColor,
} from "../../assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: "10px",
    "&.newInitiative": {
      border: "1px " + theme.palette.primary.main + " solid",
    },
  },
  cardButton: {
    padding: "0 !important",
    margin: "5px 0 0 5px !important",
    height: "22px !important",
    width: "30px !important",
    minWidth: "0 !important",
    position: "relative",
    transition: "all 0.2s",
  },
  cardButtonIcon: {
    margin: "0 !important",
    position: "absolute !important",
    top: "50% !important",
    left: "50% !important",
    transform: "translate(-50%, -50%)",
    width: "18px !important",
    height: "18px !important",
  },
  cardButtonDisabled: {
    "& svg, & .MuiSvgIcon-root": {
      opacity: "0.65 !important",
      color: "rgba(255, 255, 255, 0.65) !important",
    },
  },
  cardCategory: {
    color: theme.palette.custom?.clientTextColor || "#ffffff",
    fontSize: "14px",
    paddingTop: "5px",
    marginBottom: "0",
    marginTop: "0",
    margin: "0",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    "&.italic": {
      fontStyle: "italic !important",
      paddingRight: "4px",
    },
  },
  initiativesSpinner: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& > svg": {
      width: "40px !important",
    },
  },
  invisible: {
    opacity: "0",
    pointerEvents: "none",
  },
  inputDescription: {
    fontSize: "11px",
    marginBottom: "-2px",
  },
  topCardShortSpacing: {
    margin: "0px",
  },
  topCardBody: {
    paddingTop: "5px !important",
    paddingBottom: "5px !important",
    paddingLeft: "0px !important",
  },
  stageButtonContainer: {
    display: "flex",
    justifyContent: "center", // Center horizontally
    alignItems: "center", // Center vertically
    width: "100%",
    minWidth: "0", // Prevents flex items from overflowing
    height: "100%", // Take full height of container
  },
  stageButton: {
    margin: "0 !important",
    position: "relative",
    boxShadow: "0 0 2px rgba(0, 0, 0, 0.3) !important",
    padding: "3px 8px !important",
    height: "30px",
    fontSize: "12px",
    flex: 1, // Take equal space to fill container
    minWidth: "fit-content", // Minimum width based on content
    color: "#aaaaaa !important",
    whiteSpace: "nowrap",
  },
  stageButtonFirst: {
    borderTopRightRadius: "0 !important",
    borderBottomRightRadius: "0 !important",
  },
  stageButtonMiddle: {
    borderRadius: "0 !important",
  },
  stageButtonLast: {
    borderTopLeftRadius: "0 !important",
    borderBottomLeftRadius: "0 !important",
  },
  stageButtonSelected: {
    pointerEvents: "none",
    backgroundColor: theme.palette.primary.main + " !important",
    color: "#ffffff !important",
  },
  // Perfect Scrollbar styles
  timelineContainer: {
    // Hide native scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "&": {
      scrollbarWidth: "none", // Firefox
      msOverflowStyle: "none", // IE and Edge
    },
    "& .ps__rail-y": {
      backgroundColor: "transparent !important",
      width: "8px !important",
      right: "0px !important",
    },
    "& .ps__thumb-y": {
      backgroundColor: "rgba(0, 0, 0, 0.2) !important",
      borderRadius: "4px !important",
      width: "8px !important",
      right: "0px !important",
    },
    "& .ps__rail-y:hover .ps__thumb-y": {
      backgroundColor: "rgba(0, 0, 0, 0.3) !important",
    },
  },
  // Gradient fade-out overlay
  timelineGradientOverlay: {
    position: "absolute",
    bottom: "0",
    left: "0",
    right: "0",
    height: "50px",
    background: "linear-gradient(to bottom, transparent, #EEEEEE)",
    pointerEvents: "none",
    zIndex: 100,
  },
}));

// Helper function to get relative time
const getDateFromNow = (date) => {
  if (!date) return null;
  // Parse the date as UTC and convert to local time for display
  return moment.utc(date, "YYYY-MM-DD HH:mm").local().fromNow();
};

// Helper function to get initiative timeline stories
const getInitiativeStory = (selectedInitiative, initiatives, t) => {
  if (!selectedInitiative || selectedInitiative === "new") return [];
  
  const followUpData = initiatives[selectedInitiative]?.follow_up;
  if (!followUpData || followUpData.length === 0) return [];

  return followUpData.map((fup) => {
    const fup_color = "client";

    return {
      inverted: true,
      badgeColor: fup_color,
      badgeIcon: fup.event_type === "creation" ? OpenInBrowser : DirectionsWalk,
      title: t("initiatives.follow_up." + fup.event_type),
      titleColor: fup_color,
      trafficLight: fup.traffic_light, // Add traffic light data
      body: <p style={{ fontSize: "13px", lineHeight: "1.3", margin: "0", whiteSpace: "pre-wrap" }}>{fup.description}</p>,
      footerTitle: (
        <div style={{ fontSize: "10px" }}>
          <span style={{ color: grayColor[1] }}>{fup.user_name}</span>
          {" — "}
          <span style={{ color: grayColor[1] }}>
            {getDateFromNow(fup.date)}
          </span>
        </div>
      ),
    };
  });
};

const InitiativeExecutionCard = ({
  selectedInitiative,
  initiativeForm,
  setInitiativeFormValue,
  initiativesBusy,
  screenBusy,
  stages,
  initiatives,
  handlers,
  clientColor,
  t,
  validateMandatoryFieldsForStageChange,
  notify,
}) => {
  const classesLocal = useStyles();
  const timelineContainerRef = useRef(null);
  const psInstanceRef = useRef(null);

  useEffect(() => {
    if (timelineContainerRef.current) {
      // Initialize Perfect Scrollbar
      psInstanceRef.current = new PerfectScrollbar(timelineContainerRef.current, {
        suppressScrollX: true,
        suppressScrollY: false,
        wheelPropagation: false,
        maxScrollbarLength: 100,
        scrollingThreshold: 1000,
      });

      // Handle wheel events to prevent page scrolling
      const handleWheel = (event) => {
        event.preventDefault();
      };
      
      timelineContainerRef.current.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        if (psInstanceRef.current) {
          psInstanceRef.current.destroy();
        }
        if (timelineContainerRef.current) {
          timelineContainerRef.current.removeEventListener('wheel', handleWheel);
        }
      };
    }
  }, [selectedInitiative]); // Re-initialize when selectedInitiative changes

  // Update Perfect Scrollbar when content changes
  useEffect(() => {
    if (psInstanceRef.current) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        psInstanceRef.current.update();
      }, 100);
    }
  }, [selectedInitiative, initiatives]);

  return (
    <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      height: "100%",
      flex: 1
    }}>
      <Card
        className={cx({
          [classesLocal.card]: true,
        })}
        style={{
          marginBottom: "10px",
          height: "32px",
          backgroundImage: `linear-gradient(to right, ${blendColors(clientColor, grayColor[6], 0.5)} , ${clientColor})`,
        }}
      >
        <CardHeader
          style={{ padding: "0 6px 10px 12px", position: "relative" }}
        >
          <GridContainer>
            <GridItem>
              <p
                className={classesLocal.cardCategory}
                style={{
                  fontWeight: "400",
                  textTransform: "uppercase",
                  paddingRight: "10px",
                }}
              >
                {t("initiatives.initiative_execution")}
              </p>
            </GridItem>
            <GridItem xs={true}></GridItem>
            <GridItem
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Tooltip
                placement={"top"}
                title={t("initiatives.add_update")}
                PopperProps={{
                  style: { marginBottom: "-8px" },
                }}
              >
                <span>
                                  <Button
                  justIcon
                  color="client"
                  className={cx({
                    [classesLocal.cardButton]: true,
                    [classesLocal.cardButtonDisabled]:
                      screenBusy ||
                      initiativesBusy ||
                      selectedInitiative === "new" ||
                      (initiatives[selectedInitiative] &&
                        (initiatives[selectedInitiative].status === "cancelled" ||
                          initiatives[selectedInitiative].status === "finished"))
                  })}
                  onClick={handlers.addUpdate}
                  disabled={
                    screenBusy ||
                    initiativesBusy ||
                    selectedInitiative === "new" ||
                    (initiatives[selectedInitiative] &&
                      (initiatives[selectedInitiative].status === "cancelled" ||
                        initiatives[selectedInitiative].status === "finished"))
                  }
                >
                    <div style={{ width: "100%" }}>
                      <PostAdd className={classesLocal.cardButtonIcon} />
                    </div>
                  </Button>
                </span>
              </Tooltip>
            </GridItem>
          </GridContainer>
        </CardHeader>
      </Card>

             {/* Content area with flexbox to push timeline to bottom */}
       <div style={{ 
         display: "flex", 
         flexDirection: "column", 
         flex: 1,
         minHeight: "0"
       }}>
         {/* Stage Card */}
         <div style={{ display: "flex", flexShrink: 0, paddingBottom: "10px" }}>
           {/* Title Card - 15% width */}
           <div style={{ width: "15%", marginRight: "10px" }}>
             <Card
               className={cx({
                 [classesLocal.card]: true,
               })}
               style={{ height: "100%", margin: "0" }}
             >
               <div
                 style={{
                   color: grayColor[3],
                   backgroundColor: grayColor[8] + "44",
                   textAlign: "center",
                   padding: "15px 10px",
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   height: "100%",
                 }}
               >
                 {t("initiatives.stage")}
               </div>
             </Card>
           </div>
           {/* Content Card - 85% width */}
           <div style={{ width: "85%" }}>
             <Card
               className={cx({
                 [classesLocal.card]: true,
               })}
               style={{ height: "100%", margin: "0" }}
             >
               <CardBody
                 style={{
                   padding: "5px",
                   height: "100%",
                   display: "flex",
                   alignItems: "center",
                 }}
               >
                 <GridContainer style={{ margin: "0", width: "100%" }}>
                   <GridItem
                     xs={12}
                     style={{ display: "flex", alignItems: "center" }}
                   >
                     <div className={classesLocal.stageButtonContainer}>
                       {stages.map((stage, index) => (
                         <Button
                           key={stage}
                           size="sm"
                           simple={initiativeForm.stage !== stage}
                           className={cx(
                             classesLocal.stageButton,
                             {
                               [classesLocal.stageButtonFirst]: index === 0,
                               [classesLocal.stageButtonMiddle]:
                                 index > 0 && index < stages.length - 1,
                               [classesLocal.stageButtonLast]:
                                 index === stages.length - 1,
                             },
                             {
                               [classesLocal.stageButtonSelected]:
                                 initiativeForm.stage === stage,
                             },
                           )}
                           onClick={() => {
                             // Validate mandatory fields when changing from opportunity to other stages
                             if (initiativeForm.stage === "opportunity" && stage !== "opportunity") {
                               if (!validateMandatoryFieldsForStageChange()) {
                                 notify(t("initiatives.errors.mandatory_fields_required"), "error");
                                 return;
                               }
                             }
                             setInitiativeFormValue("stage", stage);
                           }}
                           disabled={
                             initiativesBusy ||
                             (selectedInitiative !== "new" &&
                               initiatives[selectedInitiative] &&
                               initiatives[selectedInitiative].status !== "active")
                           }
                         >
                           {t(`initiatives.stages.${stage}`)}
                         </Button>
                       ))}
                     </div>
                   </GridItem>
                 </GridContainer>
               </CardBody>
             </Card>
           </div>
         </div>

         {/* Initiative Updates Timeline */}
         <div style={{ display: "flex", marginBottom: "10px", flex: 1, minHeight: "300px", overflow: "hidden", position: "relative" }}>
            {/* Timeline Content Card - 100% width (removed title card) */}
            <div style={{ width: "100%", minHeight: "0", overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <div
                ref={timelineContainerRef}
                className={classesLocal.timelineContainer}
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: "5px",
                  paddingBottom: "10px",
                  minHeight: "0",
                  position: "relative"
                }}
              >
                {(() => {
                  const timelineStories = getInitiativeStory(selectedInitiative, initiatives, t);

                  if (timelineStories.length === 0) {
                    return (
                      <div
                        style={{
                          fontSize: "14px",
                          textAlign: "center",
                          padding: "20px",
                        }}
                      >
                        {t("initiatives.no_updates")}
                      </div>
                    );
                  }

                  return (
                    <InitiativesTimeline
                      simple
                      stories={timelineStories}
                    />
                  );
                })()}
              </div>
              {/* Gradient fade-out overlay */}
              <div className={classesLocal.timelineGradientOverlay}></div>
            </div>
          </div>
      </div>
    </div>
  );
};

InitiativeExecutionCard.propTypes = {
  // Card state and data
  selectedInitiative: PropTypes.any,
  initiativeForm: PropTypes.object.isRequired,
  setInitiativeFormValue: PropTypes.func.isRequired,

  // Status flags
  initiativesBusy: PropTypes.bool,
  screenBusy: PropTypes.bool,

  // Data arrays
  stages: PropTypes.array,
  initiatives: PropTypes.object,

  // Functions
  handlers: PropTypes.object,
  validateMandatoryFieldsForStageChange: PropTypes.func,
  notify: PropTypes.func,

  // Theme and styling
  clientColor: PropTypes.string,
  t: PropTypes.func,
};

export default InitiativeExecutionCard;
